/**
 * ContractKanbanBoard - Board do Kanban de Contratos Gerados
 *
 * Visualização alternativa da listagem de contratos, agrupada por status.
 * As colunas são FIXAS (etapas do processo de geração) e NÃO há drag & drop:
 * assim como no Kanban de filas, o card muda de coluna sozinho quando o
 * status muda no backend (via Realtime).
 *
 * Reaproveita 100% a estrutura existente: mesmos filtros da listagem,
 * mesmas permissões e o mesmo menu de ações (ContractRowActions).
 *
 * @version 1.3.0
 * RELEASE: 2026-09-26 v1.3.0 - Passa a usar a superfície compartilhada dos
 *          Kanbans somente leitura, sem mudança visual ou funcional
 * RELEASE: 2026-08-12 v1.2.0 - Scroll surface externa (overflow-hidden + overscroll-contain +
 *          bloqueio de wheel vertical) e altura explícita h-[calc(100vh-200px)] na faixa de
 *          colunas: o scroll passa a ocorrer DENTRO de cada coluna, não na página
 * RELEASE: 2026-08-12 v1.1.0 - Altura explícita da faixa de colunas + ScrollArea horizontal
 *          (mesmo padrão do Kanban de Negócios): cada coluna passa a ter altura resolvida,
 *          habilitando o scroll vertical interno dos cards até o rodapé da tela
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import React from 'react';
import { ReadonlyKanbanBoard } from '@/components/kanban/ReadonlyKanbanBoard';
import { CONTRACT_KANBAN_COLUMNS } from './contractKanbanColumns';
import { ContractKanbanColumn } from './ContractKanbanColumn';
import { useContractKanbanCounts } from '@/hooks/useContractKanbanCounts';
import { useContractsKanbanRealtime } from '@/hooks/useContractsKanbanRealtime';
import { useInvalidateContractKanban } from '@/hooks/useContractKanbanColumn';
import type { ContractFilters } from '@/hooks/useContractsPaginated';
import type { KanbanContract } from '@/hooks/useContractKanbanColumn';

interface ContractKanbanBoardProps {
  /** Tenant ativo */
  tenantId: string;
  /** Filtros compartilhados com a listagem (o filtro de status é ignorado) */
  filters?: ContractFilters;
  /** Abre o negócio vinculado ao contrato */
  onViewDeal: (dealId: string) => void;
}

export function ContractKanbanBoard({ tenantId, filters, onViewDeal }: ContractKanbanBoardProps) {
  // Realtime centralizado do board (uma única subscription por tenant)
  useContractsKanbanRealtime(true);

  // Contagem/valor por coluna em uma única consulta
  const { countsMap } = useContractKanbanCounts(filters);

  // Invalida colunas + contagens após qualquer ação de contrato
  const invalidateKanban = useInvalidateContractKanban();

  /** Clique no card abre o negócio vinculado */
  const handleSelectContract = (contract: KanbanContract) => {
    if (contract.deal_id) onViewDeal(contract.deal_id);
  };

  return (
    <ReadonlyKanbanBoard>
      {CONTRACT_KANBAN_COLUMNS.map((column) => {
        const totals = countsMap.get(column.group);
        return (
          <ContractKanbanColumn
            key={column.group}
            column={column}
            tenantId={tenantId}
            filters={filters}
            totalCount={totals?.count ?? 0}
            totalValue={totals?.value ?? 0}
            onSelectContract={handleSelectContract}
            onActionComplete={invalidateKanban}
          />
        );
      })}
    </ReadonlyKanbanBoard>
  );
}
